import React from 'react';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';

/**
 * Marca uma linha de recebimento cuja inscrição foi ISENTADA.
 *
 * Por que existe: as abas de Recebimentos excluíam `GRATUITO` da listagem desde
 * 2026-03-10, sob a regra "recebimentos é dinheiro que entrou". A PRD
 * `correcoes-reuniao-05082026` manteve essa exclusão, e o owner a reverteu em
 * QA (2026-09-01) pela alternativa que a própria PRD havia listado: incluir,
 * porém MARCADO. O marcador é o que impede uma linha de R$ 0,00 de se confundir
 * com valor efetivamente recebido.
 *
 * Isenção NÃO é o mesmo que `status === 'GRATUITO'`, e é por isso que este
 * componente lê `inscricoes`, não o status. Um pagamento nasce `GRATUITO` por
 * duas causas distintas: a categoria custa zero, ou a inscrição foi isentada.
 * Só a segunda é isenção — `InscricaoTorneio.isento`, o campo que a AC-31 criou
 * exatamente para não depender do status. Marcar pelo status chamaria de
 * "isento" todo inscrito de categoria gratuita.
 *
 * Recebe a lista de inscrições em vez de um booleano já resolvido de propósito:
 * as duas abas que o usam (torneio e liga) decidiriam o `.some()` cada uma por
 * si, e é dessa duplicação entre dois call sites que nascem as divergências.
 * Aqui a regra tem um dono só.
 *
 * Mirror de `OrigemConfirmacaoBadge`: eixo semântico próprio, nunca fundido no
 * chip de status; sem nenhum valor a marcar, renderiza `null` — não um chip
 * vazio.
 */

export interface IsentoBadgeProps {
  inscricoes?: { isento?: boolean | null }[] | null;
}

const IsentoBadge: React.FC<IsentoBadgeProps> = ({ inscricoes }) => {
  // Pagamento agrupado carrega várias inscrições; basta uma isenta para a linha
  // deixar de ser dinheiro que entrou.
  if (!inscricoes?.some((i) => i.isento === true)) {
    return null;
  }

  return (
    <Chip
      label="Isento"
      className="ps-2"
      data-testid="isento-badge"
      sx={{
        backgroundColor: 'rgba(253,126,20,0.2)',
        color: '#c25708',
        fontWeight: 500,
        '& .MuiChip-icon': { marginLeft: 0, marginRight: 0.5 },
      }}
      icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#c25708' }} />}
      size="small"
    />
  );
};

export default IsentoBadge;
